import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { fmt, fmtDateShort } from '../lib/format';
import { Calculator, CheckCircle } from '@phosphor-icons/react';

export default function Reconcile() {
  const [banks, setBanks] = useState([]);
  const [actuals, setActuals] = useState({});
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const [b, recons] = await Promise.all([
      api.get('/banks'),
      api.get('/reports/monthly', { params: { month: new Date().toISOString().slice(0, 7) } }).catch(() => ({ data: { reconciliations: [] } })),
    ]);
    setBanks(b.data);
    const init = {};
    b.data.forEach(x => { init[x.id] = x.balance; });
    setActuals(init);
    setHistory(recons.data.reconciliations || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const entries = banks.map(b => ({ bank_id: b.id, actual_balance: parseFloat(actuals[b.id] || 0) }));
      const r = await api.post('/banks/reconcile', { entries });
      setResults(r.data.results);
      load();
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="card-base p-12 text-center text-slate-500">加载中…</div>;

  return (
    <div className="space-y-5 fade-in" data-testid="reconcile-page">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">银行结算 / 对账</h1>
        <p className="text-sm text-slate-400 mt-0.5">输入各银行当前实际余额,系统自动算消耗 + 银行利润</p>
      </div>

      <div className="card-base p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Calculator size={20} weight="duotone" className="text-indigo-400" /> 输入实际余额
        </h2>
        <div className="space-y-4">
          {banks.map(b => {
            const actual = parseFloat(actuals[b.id] || 0);
            const consumed = b.balance - actual;
            const profitPct = (b.customer_fee_ref - b.bank_fee_percent) / 100;
            const profit = consumed > 0 ? consumed * profitPct : 0;
            return (
              <div key={b.id} className="p-4 rounded-lg border border-slate-700 bg-slate-900/40">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm font-semibold text-white">{b.name}</div>
                    <div className="text-xs text-slate-500 font-num">账面余额:${fmt(b.balance)}</div>
                  </div>
                  <div className="sm:w-48">
                    <label className="label-tiny block mb-1">实际余额 USD</label>
                    <input
                      type="number" step="0.01"
                      data-testid={`actual-${b.name}`}
                      className="input-base font-num"
                      value={actuals[b.id] ?? ''}
                      onChange={e => setActuals({ ...actuals, [b.id]: e.target.value })}
                    />
                  </div>
                </div>
                {!isNaN(actual) && (
                  <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-800 text-xs">
                    <Stat label="差异" value={`${consumed >= 0 ? '−' : '+'}$${fmt(Math.abs(consumed))}`} tone={consumed >= 0 ? 'normal' : 'good'} />
                    <Stat label="期间消耗" value={`$${fmt(Math.max(consumed, 0))}`} />
                    <Stat label="银行利润估算" value={`$${fmt(profit)}`} tone="good" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button onClick={submit} disabled={submitting} className="btn-primary w-full mt-5 py-3" data-testid="confirm-reconcile">
          {submitting ? '结算中…' : '✓ 确认结算'}
        </button>
      </div>

      {results && (
        <div className="card-base p-5 sm:p-6 border-emerald-500/30">
          <h2 className="text-lg font-semibold text-emerald-300 flex items-center gap-2 mb-4">
            <CheckCircle size={20} weight="duotone" /> 结算完成
          </h2>
          <div className="space-y-3">
            {results.map(r => (
              <div key={r.id} className="p-3 bg-slate-900/60 rounded-lg border border-slate-800">
                <div className="text-sm font-semibold text-white mb-1">{r.bank_name}</div>
                <div className="text-xs text-slate-400 font-num grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <span>账面 ${fmt(r.book_balance)}</span>
                  <span>实际 ${fmt(r.actual_balance)}</span>
                  <span>消耗 ${fmt(r.consumed)}</span>
                  <span className="text-emerald-400 col-span-2 sm:col-span-3 mt-1">
                    💰 银行利润估算:${fmt(r.bank_profit_estimate)} ({r.profit_percent}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-base p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-white mb-4">本月历史结算</h2>
        {history.length === 0 && <div className="text-sm text-slate-500">本月还没有结算记录</div>}
        <div className="space-y-2">
          {history.map(r => (
            <div key={r.id} className="p-3 bg-slate-900/40 rounded-lg border border-slate-800/60 text-sm">
              <div className="flex justify-between mb-1">
                <span className="font-semibold text-white">{r.bank_name}</span>
                <span className="text-xs text-slate-500 font-num">{fmtDateShort(r.created_at)} by {r.created_by}</span>
              </div>
              <div className="text-xs text-slate-400 font-num">
                账面 ${fmt(r.book_balance)} → 实际 ${fmt(r.actual_balance)} · 消耗 ${fmt(r.consumed)} · 利润 <span className="text-emerald-400">${fmt(r.bank_profit_estimate)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === 'good' ? 'text-emerald-400' : 'text-slate-200';
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500 tracking-wider">{label}</div>
      <div className={`font-num font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
